import { useForm } from '@tanstack/react-form';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { useTranslation } from 'react-i18next';
import { Link, useLoaderData, useNavigate, useRouteError } from 'react-router';
import { z } from 'zod';

import { AuthLayout } from '@cold-ambulance/layout';
import { Button, CardContent, Label } from '@cold-ambulance/shadcn';
import {
  FieldMessage,
  FormMessage,
  notifyPasswordChanged,
  PasswordInput,
} from '@cold-ambulance/ui';

import { auth } from '../../firebase.tsx';

const schema = z
  .object({
    password: z
      .string()
      .min(8, { message: 'كلمة المرور يجب أن تكون 8 أحرف على الأقل' }),
    confirmPassword: z.string().min(1, { message: 'يرجى تأكيد كلمة المرور' }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'كلمتا المرور غير متطابقتين',
    path: ['confirmPassword'],
  });

interface ResetPasswordLoaderData {
  oobCode: string;
}

export default function ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { oobCode } = useLoaderData() as ResetPasswordLoaderData;

  const form = useForm({
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
    validators: {
      onSubmit: schema,
      onSubmitAsync: schema.superRefine(async (data, ctx) => {
        try {
          const email = await verifyPasswordResetCode(auth, oobCode);
          await confirmPasswordReset(auth, oobCode, data.password);
          await notifyPasswordChanged(email);
          navigate('/password-reset');
        } catch {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'حدث خطأ أثناء تغيير كلمة المرور',
            path: ['form'],
          });
        }
        return false;
      }),
    },
  });

  return (
    <AuthLayout
      title={t('forgotPassword.setNewPassword')}
      center={true}
      subtitle={t('forgotPassword.setNewPasswordSubtitle')}
      className="bg-[#F8F8F8]"
    >
      <CardContent className="px-0">
        <form
          className="space-y-4"
          onSubmit={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            await form.handleSubmit();
          }}
        >
          <form.Field
            name="password"
            children={(field) => (
              <div className="grid w-full items-center gap-2">
                <Label className="text-sm" htmlFor="password">
                  {t('forgotPassword.newPassword')}
                </Label>
                <PasswordInput
                  id="password"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />

                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />

          <form.Field
            name="confirmPassword"
            children={(field) => (
              <div className="grid w-full items-center gap-2">
                <Label className="text-sm" htmlFor="confirmPassword">
                  {t('forgotPassword.confirmNewPassword')}
                </Label>
                <PasswordInput
                  id="confirmPassword"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />
          <form.Subscribe
            selector={(state) => state.isSubmitSuccessful}
            children={(isSubmitSuccessful) =>
              isSubmitSuccessful && (
                <div className="text-center text-green-500">
                  {t('forgotPassword.passwordChanged')}
                </div>
              )
            }
          />

          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
            children={([canSubmit, isSubmitting]) => (
              <Button
                type="submit"
                disabled={!canSubmit || isSubmitting}
                className="mt-3 w-full"
              >
                {isSubmitting ? t('common.processing') : t('common.confirm')}
              </Button>
            )}
          />

          <form.Subscribe
            selector={(state) => state.errors}
            children={(errors) => <FormMessage errors={errors} />}
          />
        </form>
      </CardContent>
    </AuthLayout>
  );
}

export function ResetPasswordError() {
  const { t } = useTranslation();
  const error = useRouteError();
  const errorMessage =
    error instanceof Error
      ? error.message
      : t('forgotPassword.invalidCode');

  return (
    <AuthLayout
      center={true}
      className="bg-[#F8F8F8]"
      title={
        <div className="flex flex-col items-center justify-center gap-y-6">
          <p className="text-red-500">{t('forgotPassword.errorOccurred')}</p>
        </div>
      }
    >
      <CardContent>
        <div className="space-y-4">
          <div className="text-center text-red-500">{errorMessage}</div>
          <div className="text-center">
            <Link to="/forgot-password" className="text-foreground underline">
              {t('forgotPassword.backToRecovery')}
            </Link>
          </div>
        </div>
      </CardContent>
    </AuthLayout>
  );
}
